import { liteClient as algoliasearch } from "algoliasearch/lite";
import type { LiteClient } from "algoliasearch/lite";

const appId = process.env.NEXT_PUBLIC_ALGOLIA_APP_ID;
const searchKey = process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_KEY;

// Fail fast on the server — this catches Next.js prerender during a Vercel
// build when env vars weren't set, so the deploy hard-fails instead of shipping
// a silently broken search. In the browser (incl. Storybook/Chromatic story
// extraction) fall back to a no-op stub: the bundle is already built, there's
// nothing a runtime throw can do, and visual regression doesn't need live
// search results.
if (typeof window === "undefined" && (!appId || !searchKey)) {
  throw new Error(
    "[algolia] Missing required env vars: NEXT_PUBLIC_ALGOLIA_APP_ID and/or NEXT_PUBLIC_ALGOLIA_SEARCH_KEY. Set these in your Vercel project (Preview + Production) or local .env before building.",
  );
}

const stubClient = {
  search: async () => ({
    results: [
      {
        hits: [],
        nbHits: 0,
        page: 0,
        nbPages: 0,
        hitsPerPage: 0,
        processingTimeMS: 0,
        query: "",
        params: "",
      },
    ],
  }),
  searchForFacetValues: async () => ({
    results: [
      { facetHits: [], exhaustiveFacetsCount: true, processingTimeMS: 0 },
    ],
  }),
} as unknown as LiteClient;

export const searchClient: LiteClient =
  appId && searchKey ? algoliasearch(appId, searchKey) : stubClient;

/**
 * Wraps a search client so every request is scoped to a single document `type`
 * and uses the directory's page size.
 *
 * The per-type directory listings need this because `<Configure>` child-widget
 * params (the type `filters` and `hitsPerPage`) aren't reliably picked up during
 * InstantSearchNext's server render — so the SSR first paint comes back
 * unfiltered (all types) and with the index-default page size, then jumps once
 * the client hydrates. Injecting these at the client level applies them to every
 * request (server and client) regardless of widget-registration timing, so the
 * first paint matches the hydrated state (no content/layout shift).
 *
 * Per-request `filters` (e.g. the events time/date range) are AND-combined with
 * the type filter. `hitsPerPage` is only set on hits requests — the disjunctive
 * facet-count requests use `hitsPerPage: 0` and are left untouched.
 *
 * `defaultFilters` covers the same SSR timing gap for a `<Configure>`-supplied
 * filter: InstantSearchNext's server render doesn't apply the widget's filter,
 * so the first paint would come back unfiltered. When a request carries no
 * `filters` of its own, `defaultFilters` is applied instead — the events
 * directory passes its default "upcoming" clause so the SSR paint shows only
 * upcoming events (matching the hydrated state). Once hydrated, `<Configure>`
 * always supplies a filter, so this fallback only ever fires on the server.
 */
export function createTypeScopedSearchClient(
  base: LiteClient,
  algoliaType: string,
  hitsPerPage: number,
  defaultFilters?: string,
): LiteClient {
  const typeFilter = `type:${algoliaType}`;
  return {
    ...base,
    search(
      requests: ReadonlyArray<{
        params?: { filters?: string; hitsPerPage?: number };
      }>,
      requestOptions?: unknown,
    ) {
      const scoped = requests.map((req) => {
        const wantsHits = req.params?.hitsPerPage !== 0;
        const reqFilters = req.params?.filters || defaultFilters;
        return {
          ...req,
          params: {
            ...(req.params ?? {}),
            filters: reqFilters
              ? `(${reqFilters}) AND ${typeFilter}`
              : typeFilter,
            ...(wantsHits ? { hitsPerPage } : {}),
          },
        };
      });
      const delegate = base.search as (
        r: unknown,
        o?: unknown,
      ) => ReturnType<LiteClient["search"]>;
      return delegate(scoped, requestOptions);
    },
  } as unknown as LiteClient;
}
